import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { UserPlus, Trash2, Users, KeyRound, Building2, Plus, X, Power } from "lucide-react";
import { toast } from "sonner";

const field = "w-full h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const label = "block text-sm font-medium mb-1.5";

const roleColors = {
  admin: "bg-[hsl(var(--positive))]/15 text-[hsl(var(--positive))]",
  accountant: "bg-primary/10 text-primary",
  cashier: "bg-accent/15 text-accent",
  viewer: "bg-muted text-muted-foreground",
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", roles: ["cashier"], company_id: "" });
  const [pwUser, setPwUser] = useState(null);
  const [pw, setPw] = useState("");
  const [mcUser, setMcUser] = useState(null);
  const [mcRows, setMcRows] = useState([]);

  const load = async () => {
    const [u, c] = await Promise.all([api.get("/users"), api.get("/companies")]);
    setUsers(u.data);
    setCompanies(c.data);
  };
  useEffect(() => { load(); }, []);

  const toggleRole = (r) =>
    setForm((f) => ({
      ...f,
      roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r],
    }));

  const save = async () => {
    try {
      if (!form.email || !form.password || !form.name || !form.company_id)
        return toast.error("All fields required");
      if (form.roles.length === 0) return toast.error("Select at least one role");
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ email: "", password: "", name: "", roles: ["cashier"], company_id: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    await api.delete(`/users/${u.id}`);
    toast.success("User deleted");
    load();
  };

  const savePw = async () => {
    try {
      if (pw.length < 6) return toast.error("Password must be at least 6 characters");
      await api.put(`/users/${pwUser.id}`, { password: pw });
      toast.success(`Password reset for ${pwUser.name}`);
      setPwUser(null);
      setPw("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openManage = (u) => {
    setMcUser(u);
    const rows = (u.companies && u.companies.length)
      ? u.companies.map((c) => ({ company_id: c.id, roles: c.roles && c.roles.length ? c.roles : ["cashier"] }))
      : [{ company_id: u.company_id || "", roles: u.tenant_roles || ["cashier"] }];
    setMcRows(rows);
  };

  const mcAddRow = () => setMcRows((r) => [...r, { company_id: "", roles: ["cashier"] }]);
  const mcRemoveRow = (i) => setMcRows((r) => r.filter((_, idx) => idx !== i));
  const mcSetCompany = (i, cid) => setMcRows((r) => r.map((row, idx) => idx === i ? { ...row, company_id: cid } : row));
  const mcToggleRole = (i, role) => setMcRows((r) => r.map((row, idx) => {
    if (idx !== i) return row;
    const roles = row.roles.includes(role) ? row.roles.filter((x) => x !== role) : [...row.roles, role];
    return { ...row, roles };
  }));

  const saveManage = async () => {
    try {
      const memberships = mcRows.filter((r) => r.company_id);
      if (memberships.length === 0) return toast.error("Assign at least one company");
      const ids = memberships.map((m) => m.company_id);
      if (new Set(ids).size !== ids.length) return toast.error("A company is selected more than once");
      if (memberships.some((m) => m.roles.length === 0)) return toast.error("Each company needs at least one role");
      await api.put(`/users/${mcUser.id}/companies`, { memberships });
      toast.success("Companies updated");
      setMcUser(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (u) => {
    const makeActive = u.active === false;
    if (!makeActive && !window.confirm(`Deactivate ${u.name}? They will not be able to log in.`)) return;
    try {
      await api.post(`/users/${u.id}/active`, { active: makeActive });
      toast.success(makeActive ? `${u.name} activated` : `${u.name} deactivated`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Layout>
      <div className="p-8 max-w-5xl mx-auto">
        <PageHeader title="Users" subtitle="Assign cashiers, accountants and viewers to companies.">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="add-user-btn" className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors">
                <UserPlus size={16} /> New User
              </button>
            </DialogTrigger>
            <DialogContent className="bg-card">
              <DialogHeader><DialogTitle className="font-display text-2xl">New User</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><label className={label}>Full name</label>
                  <input data-testid="user-name" className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={label}>Email</label>
                    <input data-testid="user-email" type="email" className={field} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                  <div><label className={label}>Password</label>
                    <input data-testid="user-password" className={field} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
                </div>
                <div>
                  <label className={label}>Company</label>
                  <select data-testid="user-company" className={field} value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })}>
                    <option value="">Select company</option>
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>Roles <span className="text-muted-foreground font-normal">(select one or more)</span></label>
                  <div className="grid grid-cols-2 gap-2">
                    {["cashier", "accountant", "viewer", "admin"].map((r) => (
                      <button key={r} type="button" data-testid={`user-role-${r}`} onClick={() => toggleRole(r)}
                        className={`h-10 rounded-md text-sm capitalize border transition-colors ${form.roles.includes(r) ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input hover:bg-muted"}`}>{r}</button>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <button data-testid="user-save" onClick={save} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Create user</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </PageHeader>

        {users.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
            <Users className="mx-auto mb-3 opacity-40" size={32} /> No users yet.
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card overflow-x-auto animate-fade-up">
            <div className="min-w-[760px]">
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_260px_110px] gap-4 px-5 py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <span>Name</span><span>Company</span><span className="text-right">Role</span><span className="text-right">Actions</span>
            </div>
            <div className="divide-y divide-border">
              {users.map((u) => (
                <div key={u.id} data-testid={`user-row-${u.id}`} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_260px_110px] gap-4 px-5 py-4 items-center">
                  <div>
                    <div className="font-medium flex items-center gap-2">{u.name}
                      {u.active === false && <span data-testid={`user-inactive-${u.id}`} className="text-[10px] px-1.5 py-0.5 rounded-full bg-destructive/10 text-destructive font-semibold uppercase">Inactive</span>}
                    </div>
                    <div className="text-xs text-muted-foreground num">{u.email}</div>
                  </div>
                  <span className="text-sm text-muted-foreground">{u.company_name}</span>
                  <div className="flex flex-wrap gap-1 justify-end">
                    {(u.companies && u.companies.length
                      ? Array.from(new Set(u.companies.flatMap((c) => c.roles || [])))
                      : (u.tenant_roles || [])
                    ).map((r) => (
                      <span key={r} className={`text-xs px-2 py-1 rounded-full capitalize font-medium ${roleColors[r] || roleColors.viewer}`}>{r}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 justify-end">
                    <button data-testid={`toggle-active-${u.id}`} onClick={() => toggleActive(u)} title={u.active === false ? "Activate user" : "Deactivate user"}
                      className={`transition-colors ${u.active === false ? "text-muted-foreground hover:text-[hsl(var(--positive))]" : "text-[hsl(var(--positive))] hover:text-destructive"}`}>
                      <Power size={16} />
                    </button>
                    <button data-testid={`manage-companies-${u.id}`} onClick={() => openManage(u)} title="Manage companies" className="text-muted-foreground hover:text-primary transition-colors">
                      <Building2 size={16} />
                    </button>
                    <button data-testid={`reset-pw-${u.id}`} onClick={() => { setPwUser(u); setPw(""); }} title="Reset password" className="text-muted-foreground hover:text-primary transition-colors">
                      <KeyRound size={16} />
                    </button>
                    <button data-testid={`del-user-${u.id}`} onClick={() => del(u)} className="text-muted-foreground hover:text-destructive transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            </div>
          </div>
        )}

        <Dialog open={!!pwUser} onOpenChange={(o) => !o && setPwUser(null)}>          <DialogContent className="bg-card">
            <DialogHeader><DialogTitle className="font-display text-2xl">Reset password · {pwUser?.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className={label}>New password</label>
                <input data-testid="admin-new-password" type="text" className={field} value={pw}
                  onChange={(e) => setPw(e.target.value)} placeholder="At least 6 characters" />
              </div>
              <p className="text-xs text-muted-foreground">Share the new password securely. The user can change it later via "Forgot password".</p>
            </div>
            <DialogFooter>
              <button data-testid="admin-save-password" onClick={savePw} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Set password</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!mcUser} onOpenChange={(o) => !o && setMcUser(null)}>
          <DialogContent className="bg-card max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-2xl">Companies · {mcUser?.name}</DialogTitle>
              <DialogDescription>Assign this user to one or more companies, each with its own role.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
              <p className="text-xs text-muted-foreground">They can switch between companies from the sidebar.</p>
              {mcRows.map((row, i) => (
                <div key={i} data-testid={`mc-row-${i}`} className="rounded-lg border border-border p-3 space-y-2.5">
                  <div className="flex items-center gap-2">
                    <select data-testid={`mc-company-${i}`} className={field} value={row.company_id} onChange={(e) => mcSetCompany(i, e.target.value)}>
                      <option value="">Select company</option>
                      {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <button data-testid={`mc-remove-${i}`} onClick={() => mcRemoveRow(i)} className="shrink-0 h-10 w-10 rounded-md border border-input flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors" title="Remove">
                      <X size={16} />
                    </button>
                  </div>
                  <div className="grid grid-cols-4 gap-1.5">
                    {["cashier", "accountant", "viewer", "admin"].map((r) => (
                      <button key={r} type="button" data-testid={`mc-role-${i}-${r}`} onClick={() => mcToggleRole(i, r)}
                        className={`h-9 rounded-md text-xs capitalize border transition-colors ${row.roles.includes(r) ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input hover:bg-muted"}`}>{r}</button>
                    ))}
                  </div>
                </div>
              ))}
              <button data-testid="mc-add-company" onClick={mcAddRow} className="w-full h-10 rounded-md border border-dashed border-input text-sm flex items-center justify-center gap-2 text-muted-foreground hover:bg-muted transition-colors">
                <Plus size={15} /> Add company
              </button>
            </div>
            <DialogFooter>
              <button data-testid="mc-save" onClick={saveManage} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Save companies</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
